var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import classnames from "classnames";
import querystring from "query-string";
import { __, getConfig, translateBackendErrorCode } from "@jti/fe-common";
import { isEvoError } from "@jti/rest-api";
import { Button, DEPRECATED_Password, DEPRECATED_TextInput, EmailOtp, ExistingAccount, FormField, Icon, Label, ReferralCode, useFormContext, useQueryStringParams, useReferralCode, validationPatterns, } from "@jti/ui";
import Edit from "@jti/ui/icons/edit.svg";
import { useAemModel } from "../../../../contexts/AemModelContext";
import { useVerifyConsumer } from "../../../../hooks/useAuth";
import { usePhoneService } from "../../../../hooks/usePhoneService";
import { useProfile } from "../../../../hooks/useProfile";
import { useSelector } from "../../../../store";
import { isString } from "../../../../utils/type-guards/isString";
import styles from "./LoginCredential.module.scss";
const defaultTimerValue = 180;
const EMAIL_FIELD = "email";
const PASSWORD_FIELD = "password";
const EMAIL_VALIDATION_CODE_KEY = "email_validation_code";
const EMAIL_VALIDATION_UUID = "email_validation_uuid";
const REFERRAL_CODE = "referral_code";
const defaultAttemptsNo = 3;
const REQUIRED_OTP_CHARS_NUMBER = 4;
const EMAIL_VALIDATION_PATTERN_ERROR_CODE = 422;
const LoginCredential = ({ onChangeReferralCode, onNext, isEmailOtpEnabled }) => {
    var _a;
    const config = getConfig();
    const otpTimer = Number.parseInt(config.REGISTRATION_EMAIL_OTP_TIMER, 10);
    const emailValidationCodeTimeout = Number.isNaN(otpTimer) ? defaultTimerValue : otpTimer;
    const attemptsNo = Number.parseInt(config.REGISTRATION_EMAIL_OTP_NUMBER_OF_ATTEMPTS, 10);
    const baseAttemptsNo = Number.isNaN(attemptsNo) ? defaultAttemptsNo : attemptsNo;
    const [hasEmailVerificationError, setEmailVerificationError] = useState(false);
    const [isEmailValidated, setEmailValidated] = useState(false);
    const [isOtpInputDisplayed, setOtpInputDisplayed] = useState(false);
    const [isPasswordBlockDisplayed, setPasswordBlockDisplayed] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [attemptLeft, setAttemptLeft] = useState(baseAttemptsNo);
    const [isAutofillTriggered, setAutofillTriggered] = useState(false);
    const isReferralOn = useSelector((state) => state.referral.isActive);
    const { emailValidation } = useAemModel();
    const { referral } = useQueryStringParams();
    const { verifyEmailOtp, isVerifyEmailOtpLoading } = usePhoneService();
    const { watch, setValue, setError, clearErrors, formState: { errors }, trigger, } = useFormContext();
    const email = watch(EMAIL_FIELD);
    const email_validation_uuid = watch(EMAIL_VALIDATION_UUID);
    const email_validation_code = watch(EMAIL_VALIDATION_CODE_KEY);
    const referral_code = watch(REFERRAL_CODE);
    const { sendEmailOTC } = useProfile();
    const { search } = useLocation();
    const { isLoading: isEmailVerificationPending, verifyConsumer } = useVerifyConsumer();
    const { isIdle, isValid, isPending, isReferralCodeActive, handleCodeValidation, handleToggleIsReferralCodeActive } = useReferralCode(referral_code);
    const hasOtpError = isOtpInputDisplayed && email_validation_code.length !== REQUIRED_OTP_CHARS_NUMBER;
    const isSubmitButtonDisabled = !email.length ||
        isEmailVerificationPending ||
        hasEmailVerificationError ||
        Object.keys(errors).length !== 0 ||
        hasOtpError ||
        isPending ||
        isVerifyEmailOtpLoading;
    const handleOTPSubmit = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (email_validation_code.length === REQUIRED_OTP_CHARS_NUMBER && attemptLeft > 0) {
            try {
                const { uuid } = yield verifyEmailOtp(email_validation_uuid, email_validation_code);
                setValue(EMAIL_VALIDATION_UUID, uuid);
                setOtpInputDisplayed(false);
                setPasswordBlockDisplayed(true);
            }
            catch (_a) {
                const attemptNo = attemptLeft - 1;
                setAttemptLeft(attemptNo);
                const message = attemptNo > 0
                    ? __("auth.registration.login_credentials.email_verify.attempts_left", [attemptNo])
                    : __("auth.registration.login_credentials.email_verify.no_attempts_left");
                setError(EMAIL_VALIDATION_CODE_KEY, {
                    type: "manual",
                    message,
                });
            }
        }
    }), [attemptLeft, email_validation_uuid, email_validation_code, setError, setValue, verifyEmailOtp]);
    useEffect(() => {
        const { email, code, email_uuid } = querystring.parse(search, {
            decode: false,
        });
        if (isString(email) && isString(code) && isString(email_uuid)) {
            setValue(EMAIL_FIELD, email);
            setValue(EMAIL_VALIDATION_UUID, email_uuid);
            setValue(EMAIL_VALIDATION_CODE_KEY, code);
            setOtpInputDisplayed(true);
            setEmailValidated(true);
            setAutofillTriggered(true);
        }
    }, [search, setValue]);
    useEffect(() => {
        if (isAutofillTriggered) {
            handleOTPSubmit();
            setAutofillTriggered(false);
        }
    }, [isAutofillTriggered, handleOTPSubmit]);
    useEffect(() => {
        if (email.length > 0 && isSubmitted) {
            trigger(EMAIL_FIELD);
        }
    }, [email, isSubmitted, trigger]);
    useEffect(() => {
        setEmailVerificationError(false);
        clearErrors(EMAIL_FIELD);
    }, [clearErrors, email]);
    useEffect(() => {
        clearErrors(EMAIL_VALIDATION_CODE_KEY);
    }, [clearErrors, email_validation_code]);
    useEffect(() => {
        clearErrors(REFERRAL_CODE);
    }, [referral_code]);
    useEffect(() => {
        if (referral && typeof referral === "string" && !isReferralCodeActive && !referral_code) {
            handleToggleIsReferralCodeActive();
            setValue(REFERRAL_CODE, referral);
        }
    }, [handleToggleIsReferralCodeActive, isReferralCodeActive, referral, referral_code, setValue]);
    const handleSendCode = useCallback(() => {
        clearErrors(EMAIL_VALIDATION_CODE_KEY);
        setValue(EMAIL_VALIDATION_CODE_KEY, "");
        sendEmailOTC(email)
            .then((data) => {
            setAttemptLeft(baseAttemptsNo);
            setValue(EMAIL_VALIDATION_UUID, data.email_verification_uuid);
            setOtpInputDisplayed(true);
        })
            .catch((error) => {
            const errorMessage = translateBackendErrorCode(error === null || error === void 0 ? void 0 : error.error_code, __("auth.registration.login_credentials.email_verify.otc.send_failed"));
            toast.error(errorMessage);
        });
    }, [email, sendEmailOTC, clearErrors, baseAttemptsNo, setValue]);
    const setFormToDefaultState = () => {
        setEmailValidated(false);
        setPasswordBlockDisplayed(false);
        setOtpInputDisplayed(false);
        setEmailVerificationError(false);
        clearErrors();
        setValue(EMAIL_VALIDATION_CODE_KEY, "");
        setValue(EMAIL_VALIDATION_UUID, "");
        setValue(PASSWORD_FIELD, "");
    };
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !isSubmitButtonDisabled && !e.isDefaultPrevented()) {
            handleSubmit(e);
        }
    };
    const verifyEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const isEmailValid = yield trigger(EMAIL_FIELD);
            if (!isEmailValid) {
                return setSubmitted(true);
            }
            const { statusCode } = yield verifyConsumer(email);
            if (String(statusCode) === "200") {
                setEmailVerificationError(true);
            }
        }
        catch (err) {
            const validationError = err;
            if ((validationError === null || validationError === void 0 ? void 0 : validationError.statusCode) === EMAIL_VALIDATION_PATTERN_ERROR_CODE) {
                setError(EMAIL_FIELD, {
                    type: "manual",
                    message: __("auth.registration.email.validation.field_pattern"),
                });
                return;
            }
            setEmailValidated(true);
            if (isEmailOtpEnabled) {
                handleSendCode();
            }
            else {
                setPasswordBlockDisplayed(true);
            }
        }
    });
    const handleReferralCodeValidation = () => __awaiter(void 0, void 0, void 0, function* () {
        if (isReferralCodeActive && referral_code && !isValid) {
            try {
                const codeValidated = yield handleCodeValidation();
                if (!codeValidated) {
                    throw new Error();
                }
                return codeValidated;
            }
            catch (e) {
                const error = isEvoError(e)
                    ? translateBackendErrorCode(e.error_code, __("auth.loyalty.errorMessage"))
                    : __("auth.loyalty.errorMessage");
                setError(REFERRAL_CODE, {
                    type: "manual",
                    message: error,
                });
                return false;
            }
        }
        return true;
    });
    const handleSubmit = (event) => __awaiter(void 0, void 0, void 0, function* () {
        event.preventDefault();
        const isReferralCodeValid = yield handleReferralCodeValidation();
        if (!isReferralCodeValid) {
            return;
        }
        onChangeReferralCode === null || onChangeReferralCode === void 0 ? void 0 : onChangeReferralCode(referral_code);
        if (!isEmailValidated) {
            return yield verifyEmail();
        }
        if (isEmailOtpEnabled && !isPasswordBlockDisplayed) {
            return yield handleOTPSubmit();
        }
        onNext();
    });
    const handleReferralToggle = () => {
        if (isReferralCodeActive) {
            setValue(REFERRAL_CODE, "");
        }
        handleToggleIsReferralCodeActive();
    };
    const submitBtnLabel = isPasswordBlockDisplayed
        ? __("auth.registration.login.next")
        : __("auth.registration.login.continue_button_label");
    const isReferralCodePrefilled = Boolean(referral);
    return (_jsxs("div", { className: styles.wrap, onKeyDown: handleKeyDown, children: [_jsx("h2", { className: styles.header, children: __("auth.registration.login.title_step") }), _jsx("div", { className: classnames(styles.emailFieldWrap, styles.inputWrapper), children: _jsx(FormField, { name: EMAIL_FIELD, controlClass: styles.controlInputWrapper, renderLabel: () => (_jsx(Label, { id: "email_control", label: __("login.email.field_label"), required: true, showRequiredAsterisk: false })), renderAdditionalContent: () => {
                        if (isEmailValidated) {
                            return (_jsxs(_Fragment, { children: [_jsx(Button, { "data-testid": "editEmailButton", className: styles.editEmailButton, color: "tertiary", onClick: setFormToDefaultState, children: __("auth.registration.login.edit_button_label") }), _jsx(Icon, { className: styles.editEmailButtonMobile, onClick: setFormToDefaultState, component: Edit, width: "32px", height: "32px" })] }));
                        }
                        return null;
                    }, render: ({ field }) => (_jsx(DEPRECATED_TextInput, Object.assign({}, field, { className: `${styles.emailField} ${hasEmailVerificationError ? styles.hasError : ""}`, dataTestId: "loginCredentialEmailField", placeholder: __("auth.registration.email.field_placeholder"), disabled: isEmailValidated, maxLength: 500 }))), validation: {
                        required: __("auth.registration.email.validation.field_required"),
                        pattern: {
                            message: __("auth.registration.email.validation.field_pattern"),
                            value: validationPatterns.EMAIL_PATTERN,
                        },
                    } }) }), isOtpInputDisplayed && (_jsx(EmailOtp, { className: styles.otpContainer, otpName: EMAIL_VALIDATION_CODE_KEY, time: emailValidationCodeTimeout, onResendCode: handleSendCode, autocompleteType: "one-time-code", isOtpNumeric: emailValidation === null || emailValidation === void 0 ? void 0 : emailValidation.isOtpNumeric })), isReferralOn && (_jsx("div", { className: styles.referralBlock, "data-testid": "referal-block", children: _jsx(ReferralCode, { fieldName: REFERRAL_CODE, isCodeValid: !isIdle && !isPending && isValid, isCodeInvalid: !isIdle && !isPending && !isValid, isActive: isReferralCodeActive, onToggle: handleReferralToggle, errorMessage: (_a = errors[REFERRAL_CODE]) === null || _a === void 0 ? void 0 : _a.message, isLocked: isReferralCodePrefilled }) })), isPasswordBlockDisplayed && (_jsx("div", { className: classnames(styles.halfBlock, styles.inputWrapper), children: _jsx(FormField, { name: PASSWORD_FIELD, required: true, renderLabel: () => (_jsx(Label, { id: "password_input", label: __("auth.registration.password.field_label"), required: true, showRequiredAsterisk: false })), render: ({ field }) => (_jsx(DEPRECATED_Password, Object.assign({}, field, { email: email, withValidationRules: true, autoComplete: "new-password", id: "password_input", className: styles.limitMaxWidth, dataTestId: "registrationPasswordField" }))) }) })), hasEmailVerificationError && (_jsx("div", { className: styles.warningWrapper, children: _jsx(ExistingAccount, { dataTestId: "existingAccount" }) })), _jsx(Button, { className: styles.button, onClick: handleSubmit, color: "primary", disabled: isSubmitButtonDisabled, "data-testid": "loginCredentialSubmitButton", children: submitBtnLabel })] }));
};
export default LoginCredential;
